import { Drop, Fire, Lightning } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { useRmDashboard } from "@/api/reseller-manager";
import { DashboardReadingsStatusCard } from "@/components/dashboard/dashboard-readings-status-card";
import { MetersBreakdownCard } from "@/components/dashboard/meters-breakdown-card";
import { QueryError } from "@/components/shared/query-error";
import { Card } from "@/components/ui/card";
import { useHideElectricity } from "@/hooks/use-hide-electricity";
import { formatConsumption } from "./dashboard-utils";
import { StatCard } from "./stat-card";

type ResellerManagerCardsProps = {
	districtId?: number | null;
	startDate?: string;
	endDate?: string;
	resellerId?: number | null;
};

export function ResellerManagerCards({
	districtId,
	startDate,
	endDate,
	resellerId,
}: ResellerManagerCardsProps) {
	const { t } = useTranslation();

	const hideElectricity = useHideElectricity();
	const { data, isLoading, isError, refetch } = useRmDashboard({
		district_id: districtId ?? undefined,
		reseller_id: resellerId ?? undefined,
		start_date: startDate,
		end_date: endDate,
	});

	const metersTotal =
		(data?.water_meters_sum ?? 0) +
		(data?.heat_meters_sum ?? 0) +
		(hideElectricity ? 0 : (data?.electricity_meters_sum ?? 0));

	const gridCols = hideElectricity
		? "@md/main:grid-cols-2 @2xl/main:grid-cols-3"
		: "@md/main:grid-cols-2 @3xl/main:grid-cols-4";

	if (isError) {
		return (
			<div className="px-4 lg:px-6">
				<Card>
					<QueryError onRetry={() => refetch()} />
				</Card>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-4 px-4 lg:px-6">
			<div className={`grid grid-cols-1 gap-4 ${gridCols}`} aria-live="polite">
				<StatCard
					icon={<Drop aria-hidden="true" className="size-5" />}
					label={t("dashboard.cards.waterConsumption")}
					value={formatConsumption(data?.water_sum)}
					loading={isLoading}
					unit="m³"
					color="blue"
					deltaPercent={data?.water_sum_change_pct ?? undefined}
					previousValue={formatConsumption(data?.water_sum_prev)}
				/>
				<StatCard
					icon={<Fire aria-hidden="true" className="size-5" />}
					label={t("dashboard.cards.heatConsumption")}
					value={formatConsumption(data?.heat_meters_total_heat)}
					loading={isLoading}
					unit="GJ"
					color="orange"
					deltaPercent={data?.heat_meters_total_heat_change_pct ?? undefined}
					previousValue={formatConsumption(data?.heat_meters_total_heat_prev)}
				/>
				{!hideElectricity && (
					<StatCard
						icon={<Lightning aria-hidden="true" className="size-5" />}
						label={t("dashboard.cards.electricityConsumption")}
						value={formatConsumption(
							data?.electricity_meters_total_consumption,
						)}
						loading={isLoading}
						unit="kWh"
						color="green"
						deltaPercent={
							data?.electricity_meters_total_consumption_change_pct ?? undefined
						}
						previousValue={formatConsumption(
							data?.electricity_meters_total_consumption_prev,
						)}
					/>
				)}
				<MetersBreakdownCard
					total={data ? metersTotal : undefined}
					water={data?.water_meters_sum}
					heat={data?.heat_meters_sum}
					electricity={
						hideElectricity ? undefined : data?.electricity_meters_sum
					}
					loading={isLoading}
				/>
			</div>
			<DashboardReadingsStatusCard
				nextReadingDate={data?.next_reading_date}
				isLoading={isLoading}
			/>
		</div>
	);
}
